import React, { useEffect, useRef, useState } from 'react';
import { track, EVENTS } from '../services/analytics';

// The first two minutes, in screens (founder, 2026-09-18: "like a video, but only made of screens, and the
// typing should look real"). Five scenes drawn from the product's own chrome and its own words: the strings
// here are the strings the builder shows, so the page cannot drift into promising a screen that does not exist.
//
// It stops when it is off the screen, it never plays for a visitor who asked for less motion, and the step
// labels are buttons, so the sequence can be driven by hand and by keyboard rather than only watched.
const ADDRESS = 'sidecount.example';   // a reserved TLD: the rest of the repo's examples use it, and it belongs to nobody
const SCENES = [
  { id: 'paste', label: 'You paste your address', ms: 3400 },
  { id: 'read', label: 'It reads the page', ms: 3000 },
  { id: 'look', label: 'You pick a look', ms: 3000 },
  { id: 'write', label: 'It writes the session', ms: 3600 },
  { id: 'room', label: 'You schedule it in Zoom', ms: 3600 },
];
const TYPE_MS = 85;                     // a character at a time, about as fast as a person types an address

export default function BuildFilm() {
  const reduced = usePrefersReducedMotion();
  // Without motion the sequence cannot tell its story, so it opens on the end of it: the scheduled session, the
  // one screen that says what all five were for. The step buttons still walk through the rest.
  const [i, setI] = useState(reduced ? SCENES.length - 1 : 0);
  const [typed, setTyped] = useState(reduced ? ADDRESS.length : 0);
  const [playing, setPlaying] = useState(!reduced);
  const held = useRef(false);            // Pause was asked for by hand; scrolling must not undo it (reviewer, #63)
  const [clock, setClock] = useState(24);
  const box = useRef(null);
  const watched = useRef(false);

  // Off the screen is not worth a timer, and a page that animates in a background tab is a rude one.
  useEffect(() => {
    if (reduced || !box.current || typeof IntersectionObserver !== 'function') return undefined;
    const io = new IntersectionObserver(([e]) => setPlaying(e.isIntersecting && !held.current), { threshold: 0.35 });
    io.observe(box.current);
    return () => io.disconnect();
  }, [reduced]);

  useEffect(() => {
    if (!playing || reduced) return undefined;
    const id = setTimeout(() => {
      const next = (i + 1) % SCENES.length;
      if (next === 0 && !watched.current) { watched.current = true; track(EVENTS.LANDING_FILM_WATCHED, { scenes: SCENES.length }); }
      setI(next); setTyped(next === 0 ? 0 : ADDRESS.length); setClock(24);
    }, SCENES[i].ms);
    return () => clearTimeout(id);
  }, [i, playing, reduced]);

  useEffect(() => {
    if (!playing || reduced || SCENES[i].id !== 'paste' || typed >= ADDRESS.length) return undefined;
    const id = setTimeout(() => setTyped((t) => t + 1), TYPE_MS);
    return () => clearTimeout(id);
  }, [i, typed, playing, reduced]);

  useEffect(() => {
    if (!playing || reduced || SCENES[i].id !== 'write') return undefined;
    const id = setInterval(() => setClock((c) => c + 1), 1000);
    return () => clearInterval(id);
  }, [i, playing, reduced]);

  const go = (n) => { held.current = true; setI(n); setTyped(n === 0 ? 0 : ADDRESS.length); setClock(24); setPlaying(false); };
  const toggle = () => { const next = !playing; held.current = !next; setPlaying(next); };
  const scene = SCENES[i];

  return (
    <section className="film-band" id="watch">
      <div className="wrap">
        <h2>The first two minutes, in screens</h2>
        <p className="sub film-sub">Fullrun&apos;s own screens, in the order you meet them. Sidecount is an example
          product; the deck it draws is the one in the example, 13 slides and 4 polls.</p>

        <div className="film" ref={box}>
          <div className="film-stage" role="group" aria-label={`Screen ${i + 1} of ${SCENES.length}: ${scene.label}`}>
            {scene.id === 'paste' && (
              <div className="shot shot-paste">
                <div className="chrome"><span className="dots" aria-hidden="true"><i /><i /><i /></span><span className="addr">fullrun.io</span></div>
                <div className="body">
                  <p className="film-h1">Your landing page becomes next week&apos;s webinar.</p>
                  <div className="page-door" aria-hidden="true">
                    <span className="field"><span className="scheme">https://</span>
                      <span className="typing">{ADDRESS.slice(0, typed)}<i className={`caret${typed >= ADDRESS.length ? ' done' : ''}`} /></span>
                    </span>
                    <span className="btn primary">Read my page</span>
                  </div>
                  <p className="film-note">No page yet? Answer seven questions instead. Either way you sign in
                    once, so the deck is yours and stays yours.</p>
                </div>
              </div>
            )}

            {scene.id === 'read' && (
              <div className="shot">
                <div className="chrome"><span className="dots" aria-hidden="true"><i /><i /><i /></span><span className="addr">fullrun.io/app</span></div>
                <div className="body">
                  <div className="steps">Reading the page</div>
                  <ol className="stages">
                    <li className="stage done"><span className="i">{'✓'}</span> <span>The page read</span> <span className="m">{ADDRESS}</span>
                      <div className="sub">
                        <div className="found"><span className="k">found </span>the claim: Know where the money went, every Friday</div>
                        <div className="found"><span className="k">found </span>2 quotes: I knew what I had billed.</div>
                        <div className="missing">did not find a price</div>
                      </div>
                    </li>
                    <li className="stage now"><span className="i">{'●'}</span> <span>Three looks from your page</span></li>
                  </ol>
                </div>
              </div>
            )}

            {scene.id === 'look' && (
              <div className="shot">
                <div className="chrome"><span className="dots" aria-hidden="true"><i /><i /><i /></span><span className="addr">fullrun.io/app</span></div>
                <div className="body">
                  <p className="film-q">Which look should the deck wear?</p>
                  <p className="film-how">Read from {ADDRESS}: three ways to wear its colours. Tap one; the title slide is drawn in it.</p>
                  <div className="film-looks">
                    {[{ n: "The page's own", bg: '#12181F', fg: '#FBFBF8', src: 'primary #12181F: theme-color meta', on: true },
                      { n: 'Ink and accent', bg: '#8F5708', fg: '#FBFBF8', src: 'primary #8F5708: 41 uses in the page styles' },
                      { n: 'Warm', bg: '#7A2E1F', fg: '#FBFBF8', src: 'a warm pairing from the deck runtime' }].map((l) => (
                      <div className={`film-look${l.on ? ' on' : ''}`} key={l.n}>
                        <div className="slide" style={{ background: l.bg, color: l.fg }}>Where the money went</div>
                        <div className="nm">{l.n}</div>
                        <div className="src">{l.src}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {scene.id === 'write' && (
              <div className="shot">
                <div className="chrome"><span className="dots" aria-hidden="true"><i /><i /><i /></span><span className="addr">fullrun.io/app</span></div>
                <div className="body">
                  <div className="steps">Drafting</div>
                  <ol className="stages">
                    <li className="stage done"><span className="i">{'✓'}</span> <span>The page read</span> <span className="m">{ADDRESS}</span></li>
                    <li className="stage done"><span className="i">{'✓'}</span> <span>The look chosen</span> <span className="m">The page&apos;s own</span></li>
                    <li className="stage done"><span className="i">{'✓'}</span> <span>The shape planned</span> <span className="m">13 slides, 4 polls, 33:30</span></li>
                    <li className="stage now"><span className="i">{'●'}</span> <span>Every slide written in the page&apos;s words</span> <span className="m">0:{String(clock).padStart(2, '0')}</span>
                      <div className="sub">Usually 20 to 40 seconds. Nothing to show until the whole deck is back.</div>
                    </li>
                    <li className="stage todo"><span className="i">{'○'}</span> <span>Every number checked against the page and your answers</span></li>
                  </ol>
                </div>
              </div>
            )}

            {scene.id === 'room' && (
              <div className="shot">
                <div className="chrome"><span className="dots" aria-hidden="true"><i /><i /><i /></span><span className="addr">fullrun.io/app</span></div>
                <div className="body film-room">
                  <div className="film-deck" style={{ background: '#12181F' }}>
                    <div className="k">Where the money went</div>
                    <div className="b">Sidecount, Thursday October 8, 12:00 PT</div>
                  </div>
                  <div className="film-sched">
                    <div className="chips" aria-hidden="true"><span className="chip on">Meeting</span><span className="chip">Webinar</span></div>
                    <span className="pill ok">Scheduled in Zoom meeting, 4 polls loaded</span>
                    <dl className="kv">
                      <dt>When</dt><dd>2026-10-08 at 12:00, 34 minutes</dd>
                      <dt>Join link</dt><dd>us06web.zoom.us/j/81234567890</dd>
                    </dl>
                    <p className="muted">Record in Zoom as usual. When the recording is ready it arrives here as a
                      run, measured against this deck.</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <ol className="film-steps">
            {SCENES.map((s, n) => (
              <li key={s.id}>
                <button type="button" className={n === i ? 'on' : ''} aria-current={n === i ? 'step' : undefined} onClick={() => go(n)}>
                  <span className="bar" aria-hidden="true"><span className={n === i && playing && !reduced ? 'fill run' : 'fill'} style={{ animationDuration: `${s.ms}ms` }} /></span>
                  {s.label}
                </button>
              </li>
            ))}
          </ol>
          {!reduced && (
            <p className="film-controls">
              <button type="button" className="linklike" onClick={toggle}>{playing ? 'Pause' : 'Play'}</button>
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(() => {
    try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
  });
  useEffect(() => {
    let mq;
    try { mq = window.matchMedia('(prefers-reduced-motion: reduce)'); } catch { return undefined; }
    const on = () => setReduced(mq.matches);
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  return reduced;
}
